import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { toast } from 'react-toastify';
import './ProxyForm.css';
import Logo from '../../assets/updated_hamsa_logo_transparent.png';
import proxyServices from '../../services/proxyServices';

const ProxyForm = () => {
  const [formData, setFormData] = useState({
    // Meeting Information
    meeting_date: '',
    location: '5161 rice rd',
    
    // Member Information
    member_name: '',
    member_id: '',
    member_phone: '',
    member_email: '',

    // Proxy Representative
    proxy_name: '',
    proxy_id: '',
    proxy_phone: '',
    
    // Member Authorization
    member_signature: '',
    member_printed_name: '',
    member_auth_date: '',
    
    // Proxy Acceptance
    proxy_accept_name: '',
    proxy_accept_date: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = [
      'meeting_date', 'location', 'member_name', 'member_id', 'member_phone', 
      'member_email',
      'proxy_name', 'proxy_id', 'proxy_phone', 'member_signature',
      'member_printed_name', 'member_auth_date', 'proxy_accept_name', 'proxy_accept_date'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.member_email && !emailRegex.test(formData.member_email)) {
      newErrors.member_email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const submissionData = {
        meeting: {
          date: formData.meeting_date,
          location: formData.location
        },
        member: {
          name: formData.member_name,
          id: formData.member_id,
          phone: formData.member_phone,
          email: formData.member_email
        },
        proxy: {
          name: formData.proxy_name,
          id: formData.proxy_id,
          phone: formData.proxy_phone
        },
        authorization: {
          member_signature: formData.member_signature,
          member_printed_name: formData.member_printed_name,
          date: formData.member_auth_date
        },
        acceptance: {
          proxy_name: formData.proxy_accept_name,
          date: formData.proxy_accept_date
        },
        createdAt: new Date().toISOString()
      };

      // Make the API call using the service
      const response = await proxyServices.submitProxyForm(submissionData);
      
      if (response.data.success) {
        toast.success('Proxy form submitted successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to submit proxy form');
      }
      
      // Reset form
      setFormData({
        meeting_date: '',
        location: '5161 rice rd',
        member_name: '',
        member_id: '',
        member_phone: '',
        member_email: '',
        proxy_name: '',
        proxy_id: '',
        proxy_phone: '',
        member_signature: '',
        member_printed_name: '',
        member_auth_date: '',
        proxy_accept_name: '',
        proxy_accept_date: ''
      });
      setErrors({});

    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Error:', error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const response = await fetch(`${baseURL}/proxy-form-pdf`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', errorText);
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Hamsa_Lomi_Proxy_Form.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    }
  };

  return (
    <Box className="proxy-form-container">
      {/* Download PDF Button at the top */}
      <Box className="pdf-download-section no-print">
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleDownloadPDF}
          sx={{ 
            mb: 3, 
            backgroundColor: '#28a745',
            '&:hover': { backgroundColor: '#218838' },
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '10px 20px'
          }}
        >
          📄 Download PDF
        </Button>
      </Box>

      <Box className="form-container" id="proxy-form-content">
        {/* Header with logos and Amharic text */}
        <Box className="form-header-with-logos">
          <img src={Logo} alt="Hamsa Lomi Logo" className="header-logo left-logo" />
          <Box className="header-center">
            <Typography variant="h6" className="amharic-text">
              ሃምሳ ሎሚ የኢትዮያውያን  ማህበር - ናሽቪል - ቴነሲ
            </Typography>
            <Typography variant="h4" className="form-title">
              HAMSA LOMI ETHIOPIAN ASSOCIATION PROXY FORM
            </Typography>
          </Box>
          <img src={Logo} alt="Hamsa Lomi Logo" className="header-logo right-logo" />
        </Box>
        
        <Box className="form-header">
          <Typography variant="body2" className="form-notice">
            When you submit this form, it will not automatically collect your details like name and email address unless you provide it yourself.
          </Typography>
          <Typography variant="body2" className="required-field">
            * Required
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} className="proxy-form">
          {/* Meeting Information Section */}
          <Box className="form-section">
            <Typography variant="h5" className="section-title">Meeting Information</Typography>
            
            <Box className="form-group">
              <label htmlFor="meeting-date" className="required">1. Date of meeting</label>
              <input 
                type="date" 
                id="meeting-date" 
                name="meeting_date" 
                value={formData.meeting_date}
                onChange={handleInputChange}
                required 
              />
              {errors.meeting_date && <div className="error">{errors.meeting_date}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="location" className="required">2. Location</label>
              <input 
                type="text" 
                id="location" 
                name="location" 
                value={formData.location}
                onChange={handleInputChange}
                required 
              />
              {errors.location && <div className="error">{errors.location}</div>}
            </Box>
          </Box>

          {/* Member Information Section */}
          <Box className="form-section">
            <Typography variant="h5" className="section-title">Member Information</Typography>
            
            <Box className="form-group">
              <label htmlFor="member-name" className="required">3. Name</label>
              <input 
                type="text" 
                id="member-name" 
                name="member_name" 
                value={formData.member_name}
                onChange={handleInputChange}
                required 
              />
              {errors.member_name && <div className="error">{errors.member_name}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="member-id" className="required">4. Membership ID</label>
              <input 
                type="text" 
                id="member-id" 
                name="member_id" 
                value={formData.member_id}
                onChange={handleInputChange}
                required 
              />
              {errors.member_id && <div className="error">{errors.member_id}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="member-phone" className="required">5. Phone</label>
              <input 
                type="tel" 
                id="member-phone" 
                name="member_phone" 
                value={formData.member_phone}
                onChange={handleInputChange}
                required 
              />
              {errors.member_phone && <div className="error">{errors.member_phone}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="member-email" className="required">6. Email</label>
              <input 
                type="email" 
                id="member-email" 
                name="member_email" 
                value={formData.member_email}
                onChange={handleInputChange}
                required 
              />
              {errors.member_email && <div className="error">{errors.member_email}</div>}
            </Box>
          </Box>

          {/* Proxy Representative Section */}
          <Box className="form-section">
            <Typography variant="h5" className="section-title">Proxy Representative</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              I, the undersigned member of the Hamsa Lomi Ethiopian Association, hereby appoint
            </Typography>
            
            <Box className="form-group">
              <label htmlFor="proxy-name" className="required">7. Name</label>
              <input 
                type="text" 
                id="proxy-name" 
                name="proxy_name" 
                value={formData.proxy_name}
                onChange={handleInputChange}
                required 
              />
              {errors.proxy_name && <div className="error">{errors.proxy_name}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="proxy-id" className="required">8. Membership ID</label>
              <input 
                type="text" 
                id="proxy-id" 
                name="proxy_id" 
                value={formData.proxy_id}
                onChange={handleInputChange}
                required 
              />
              {errors.proxy_id && <div className="error">{errors.proxy_id}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="proxy-phone" className="required">9. Phone</label>
              <input 
                type="tel" 
                id="proxy-phone" 
                name="proxy_phone" 
                value={formData.proxy_phone}
                onChange={handleInputChange}
                required 
              />
              {errors.proxy_phone && <div className="error">{errors.proxy_phone}</div>}
            </Box>
          </Box>

          {/* Member Authorization Section */}
          <Box className="form-section">
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
              As my proxy to attend, act, and vote on my behalf at the general assembly meeting
            </Typography>
            
            <Box className="form-group">
              <label htmlFor="member-signature" className="required">10. Signature of member</label>
              <input 
                type="text" 
                id="member-signature" 
                name="member_signature" 
                value={formData.member_signature}
                onChange={handleInputChange}
                required 
              />
              {errors.member_signature && <div className="error">{errors.member_signature}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="member-printed-name" className="required">11. Printed name</label>
              <input 
                type="text" 
                id="member-printed-name" 
                name="member_printed_name" 
                value={formData.member_printed_name}
                onChange={handleInputChange}
                required 
              />
              {errors.member_printed_name && <div className="error">{errors.member_printed_name}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="member-auth-date" className="required">12. Date</label>
              <input 
                type="date" 
                id="member-auth-date" 
                name="member_auth_date" 
                value={formData.member_auth_date}
                onChange={handleInputChange}
                required 
              />
              {errors.member_auth_date && <div className="error">{errors.member_auth_date}</div>}
            </Box>
          </Box>

          {/* Proxy Acceptance Section */}
          <Box className="form-section">
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
              I accept the above appointment as proxy
            </Typography>
            
            <Box className="form-group">
              <label htmlFor="proxy-accept-name" className="required">13. Name of proxy</label>
              <input 
                type="text" 
                id="proxy-accept-name" 
                name="proxy_accept_name" 
                value={formData.proxy_accept_name}
                onChange={handleInputChange}
                required 
              />
              {errors.proxy_accept_name && <div className="error">{errors.proxy_accept_name}</div>}
            </Box>
            
            <Box className="form-group">
              <label htmlFor="proxy-accept-date" className="required">14. Date</label>
              <input 
                type="date" 
                id="proxy-accept-date" 
                name="proxy_accept_date" 
                value={formData.proxy_accept_date}
                onChange={handleInputChange}
                required 
              />
              {errors.proxy_accept_date && <div className="error">{errors.proxy_accept_date}</div>}
            </Box>
          </Box>

          <Box className="form-navigation">
            <Button 
              type="button" 
              variant="outlined" 
              onClick={() => window.history.back()}
              sx={{
                borderColor: '#28a745',
                color: '#28a745',
                '&:hover': { 
                  borderColor: '#218838',
                  backgroundColor: 'rgba(40, 167, 69, 0.04)'
                }
              }}
            >
              Back
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
                fontWeight: 'bold'
              }}
            >
              Submit
            </Button>
          </Box>

          <Typography variant="body2" className="disclaimer">
            Never give out your password. Report abuse
          </Typography>
        </form>
      </Box>
    </Box>
  );
};

export default ProxyForm;
